/**
 * Builds ActiveFilter[] for the export modal by reverse-mapping raw filter
 * values to their display labels using the same filter options the dropdowns use.
 *
 * Eliminates per-section special-case logic (status label maps, severity enum
 * lookups, origin collapsing) by reading labels straight from the source of truth.
 */

import type { ActiveFilter } from '@/components/export/universal/types';
import type { FilterOption, GroupOption } from '@/components/filters/types';

type FilterOptions = Record<string, (FilterOption | GroupOption)[]>;

/**
 * Given the raw advancedFilters object and the filter option definitions,
 * produce a list of ActiveFilter pills for the export modal.
 *
 * For grouped options (e.g. "Praetorian" origin group) the values are
 * collapsed back to the group name when every member is selected.
 */
export function buildActiveFilters(
  advancedFilters: Record<string, unknown> | undefined,
  filterOptions: FilterOptions,
  search?: string,
  /** Optional map of filter key → display label (e.g. { attackSurface: 'Attack Surface' }) */
  fieldLabels?: Record<string, string>
): ActiveFilter[] {
  const result: ActiveFilter[] = [];

  if (search) {
    result.push({ label: 'Search', values: [search] });
  }

  if (!advancedFilters) return result;

  for (const [key, raw] of Object.entries(advancedFilters)) {
    // Range filters (e.g. cvss/epss { min, max })
    if (isRangeValue(raw)) {
      const label = findFieldLabel(key, fieldLabels);
      result.push({ label, values: [`${raw.min} – ${raw.max}`] });
      continue;
    }

    if (!Array.isArray(raw) || raw.length === 0) continue;

    const values = raw.map(String);
    const options = filterOptions[key];
    const label = findFieldLabel(key, fieldLabels);

    if (!options || options.length === 0) {
      // No option metadata available — show raw values
      result.push({ label, values });
      continue;
    }

    result.push({ label, values: collapseToLabels(values, options) });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Collapse selected values back to group names where possible, map the rest to labels. */
function collapseToLabels(
  selectedValues: string[],
  options: (FilterOption | GroupOption)[]
): string[] {
  const selectedSet = new Set(selectedValues);
  const consumed = new Set<string>();
  const labels: string[] = [];

  // First pass: detect fully-selected groups
  for (const opt of options) {
    if (!isGroup(opt) || !opt.items?.length) continue;

    const allSelected = opt.items.every(item =>
      selectedSet.has(String(item.value))
    );
    if (allSelected) {
      labels.push(opt.label);
      opt.items.forEach(item => consumed.add(String(item.value)));
    }
  }

  // Second pass: remaining values — look up individual labels
  for (const val of selectedValues) {
    if (consumed.has(val)) continue;
    const found = findOptionLabel(val, options);
    labels.push(found ?? val);
  }

  return labels;
}

/** Look up the display label of a single value across flat + grouped options. */
function findOptionLabel(
  value: string,
  options: (FilterOption | GroupOption)[]
): string | undefined {
  for (const opt of options) {
    if (String(opt.value) === value) return opt.label;
    if (isGroup(opt) && opt.items) {
      const child = opt.items.find(item => String(item.value) === value);
      if (child) return child.label;
    }
  }
  return undefined;
}

/** Derive a human-readable field label from the label map or the key itself. */
function findFieldLabel(
  key: string,
  fieldLabels?: Record<string, string>
): string {
  return fieldLabels?.[key] ?? humanizeKey(key);
}

/** Convert camelCase/snake_case key to Title Case label */
function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function isGroup(opt: FilterOption | GroupOption): opt is GroupOption {
  return 'isGroup' in opt && !!opt.isGroup && Array.isArray(opt.items);
}

function isRangeValue(v: unknown): v is { min: string; max: string } {
  return (
    typeof v === 'object' &&
    v !== null &&
    'min' in v &&
    'max' in v &&
    !Array.isArray(v)
  );
}
